import type {
  ConfigValue,
  Customer,
  DayCalendarEntry,
  EventType,
  ForecastResult,
  HistoricalOrder,
  Warehouse,
} from '@/domain';
import { runForecast, type ForecastInput, type ForecastOutput } from '@/engine/forecast';
import type { HistoricalSample } from '@/engine/forecast/historical-aggregator';
import { mockStore } from '@/lib/mock-store';
import { getSupabase, isMockBackend } from '@/lib/supabase';
import { auditService } from './auditService';

interface RunOptions {
  warehouse_id: string;
  customer_id?: string; // undefined → all active customers
  start_date: string; // inclusive
  end_date: string; // inclusive
  history_window_days?: number; // default 90
  actor: string;
}

interface RunSummary {
  rows: ForecastResult[];
  warehouses: number;
  customers: number;
  days: number;
}

function randomId(): string {
  return `fc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function isoSubtract(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function dateRange(start: string, end: string): string[] {
  const out: string[] = [];
  const cur = new Date(`${start}T00:00:00Z`);
  const stop = new Date(`${end}T00:00:00Z`);
  while (cur <= stop) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

async function loadInputs() {
  if (isMockBackend) {
    return {
      warehouses: mockStore.getAll('warehouses'),
      customers: mockStore.getAll('customers'),
      calendar: mockStore.getAll('day_calendar'),
      history: mockStore.getAll('historical_orders'),
      configs: mockStore.getAll('config_values'),
    };
  }
  const sb = getSupabase()!;
  const [w, c, cal, h, cfg] = await Promise.all([
    sb.from('warehouses').select('*'),
    sb.from('customers').select('*'),
    sb.from('day_calendar').select('*'),
    sb.from('historical_orders').select('*'),
    sb.from('config_values').select('*').eq('is_current', true),
  ]);
  if (w.error || c.error || cal.error || h.error || cfg.error) {
    throw new Error('forecastService.loadInputs failed');
  }
  return {
    warehouses: (w.data ?? []) as Warehouse[],
    customers: (c.data ?? []) as Customer[],
    calendar: (cal.data ?? []) as DayCalendarEntry[],
    history: (h.data ?? []) as HistoricalOrder[],
    configs: (cfg.data ?? []) as ConfigValue[],
  };
}

interface ResolvedConfigs {
  monthly: Record<number, number>;
  event: Record<EventType, number>;
  uph: Map<string, number>;
  versions: Record<string, number>;
}

function resolveConfigs(configs: ConfigValue[]): ResolvedConfigs {
  const monthly: Record<number, number> = {};
  const event: Record<EventType, number> = {
    NORMAL: 1,
    PAYDAY: 1,
    MID_MONTH: 1,
    MINI_SPIKE: 1,
    DOUBLE_DAY: 1,
    MEGA_SALE: 1,
  };
  const uph = new Map<string, number>();
  const versions: Record<string, number> = {};
  for (const v of configs) {
    if (!v.is_current) continue;
    if (v.kind === 'MONTHLY_MULTIPLIER') {
      const m = Number(v.key.split(':')[1]);
      if (!Number.isNaN(m)) monthly[m] = v.value;
    } else if (v.kind === 'EVENT_MULTIPLIER') {
      const e = v.key.split(':')[1] as EventType;
      if (e) event[e] = v.value;
    } else if (v.kind === 'UPH_NORM') {
      uph.set(v.key, v.value);
    }
    versions[v.kind] = Math.max(versions[v.kind] ?? 0, v.version);
  }
  return { monthly, event, uph, versions };
}

function persist(rows: ForecastResult[]): void {
  if (isMockBackend) {
    const existing = mockStore.getAll('forecast_results');
    // Demote prior current for matching (warehouse, customer, date) tuples
    const newKeys = new Set(rows.map((r) => `${r.warehouse_id}|${r.customer_id}|${r.date}`));
    const demoted = existing.map((r) => {
      const k = `${r.warehouse_id}|${r.customer_id}|${r.date}`;
      if (r.is_current && newKeys.has(k)) return { ...r, is_current: false };
      return r;
    });
    mockStore.replace('forecast_results', [...demoted, ...rows]);
    return;
  }
  // Supabase path: trigger handles demote
  const sb = getSupabase()!;
  void sb.from('forecast_results').insert(rows);
}

async function priorVersion(
  warehouseId: string,
  customerId: string,
  date: string,
): Promise<number> {
  if (isMockBackend) {
    const matching = mockStore
      .getAll('forecast_results')
      .filter(
        (r) =>
          r.warehouse_id === warehouseId && r.customer_id === customerId && r.date === date,
      );
    return Math.max(0, ...matching.map((r) => r.version));
  }
  const sb = getSupabase()!;
  const { data } = await sb
    .from('forecast_results')
    .select('version')
    .eq('warehouse_id', warehouseId)
    .eq('customer_id', customerId)
    .eq('date', date)
    .order('version', { ascending: false })
    .limit(1);
  return data?.[0]?.version ?? 0;
}

export const forecastService = {
  async list(opts?: { warehouseId?: string; date?: string }): Promise<ForecastResult[]> {
    if (isMockBackend) {
      let rows = mockStore.getAll('forecast_results').filter((r) => r.is_current);
      if (opts?.warehouseId) rows = rows.filter((r) => r.warehouse_id === opts.warehouseId);
      if (opts?.date) rows = rows.filter((r) => r.date === opts.date);
      return rows.sort((a, b) => a.date.localeCompare(b.date));
    }
    const sb = getSupabase()!;
    let q = sb.from('forecast_results').select('*').eq('is_current', true);
    if (opts?.warehouseId) q = q.eq('warehouse_id', opts.warehouseId);
    if (opts?.date) q = q.eq('date', opts.date);
    const { data, error } = await q.order('date', { ascending: true });
    if (error) throw error;
    return (data ?? []) as ForecastResult[];
  },

  /**
   * Run the forecast for a (warehouse × [customer?] × date-range) batch.
   * Resolves all inputs, calls the pure engine per cell, persists results,
   * and emits one audit_log entry for the whole batch.
   */
  async run(opts: RunOptions): Promise<RunSummary> {
    const { warehouses, customers, calendar, history, configs } = await loadInputs();
    const resolved = resolveConfigs(configs);
    const calMap = new Map(calendar.map((c) => [c.date, c]));

    const wh = warehouses.find((w) => w.id === opts.warehouse_id);
    if (!wh) throw new Error(`Unknown warehouse_id: ${opts.warehouse_id}`);

    const targetCustomers = opts.customer_id
      ? customers.filter((c) => c.id === opts.customer_id && c.is_active)
      : customers.filter((c) => c.is_active);

    const dates = dateRange(opts.start_date, opts.end_date);
    const historyWindowDays = opts.history_window_days ?? 90;
    const earliestHistoryStart = isoSubtract(opts.start_date, historyWindowDays);

    const out: ForecastResult[] = [];
    for (const cust of targetCustomers) {
      // Pre-filter history once per (warehouse × customer) — small set
      const samples: HistoricalSample[] = history
        .filter(
          (h) =>
            h.warehouse_id === wh.id &&
            h.customer_id === cust.id &&
            h.date >= earliestHistoryStart &&
            h.date <= opts.end_date,
        )
        .map((h) => ({
          date: h.date,
          qty: h.qty,
          event_type: calMap.get(h.date)?.event_type ?? 'NORMAL',
        }));

      for (const date of dates) {
        const cal = calMap.get(date);
        const eventType = cal?.event_type ?? 'NORMAL';
        const dayType = cal?.day_type ?? 'WEEKDAY';
        const input: ForecastInput = {
          warehouse_id: wh.id,
          customer_id: cust.id,
          target_date: date,
          target_event_type: eventType,
          target_day_type: dayType,
          history: samples,
          history_window_start: earliestHistoryStart,
          history_window_end: opts.end_date,
          monthly_multipliers: resolved.monthly,
          event_multipliers: resolved.event,
          uph_norms: resolved.uph,
          product_mix: cust.product_mix,
          config_versions: resolved.versions,
        };
        const engineOut: ForecastOutput = runForecast(input);

        const prior = await priorVersion(wh.id, cust.id, date);
        out.push({
          id: randomId(),
          warehouse_id: engineOut.warehouse_id,
          customer_id: engineOut.customer_id,
          date: engineOut.date,
          event_type: engineOut.event_type,
          day_type: engineOut.day_type,
          forecast_qty: engineOut.forecast_qty,
          wlu_per_order_minutes: engineOut.wlu_per_order_minutes,
          total_workload_minutes: engineOut.total_workload_minutes,
          total_workload_hours: engineOut.total_workload_hours,
          provenance: engineOut.provenance,
          confidence: engineOut.confidence,
          version: prior + 1,
          is_current: true,
          generated_at: new Date().toISOString(),
          generated_by: opts.actor,
        });
      }
    }

    persist(out);

    await auditService.log({
      actor: opts.actor,
      action: 'FORECAST_RUN',
      entity_type: 'forecast_results',
      summary: `Chạy dự báo ${wh.code} · ${targetCustomers.length} khách hàng × ${dates.length} ngày = ${out.length} rows`,
      payload: {
        warehouse: wh.code,
        customers: targetCustomers.length,
        days: dates.length,
        rows: out.length,
        start: opts.start_date,
        end: opts.end_date,
        config_versions: resolved.versions,
      },
    });

    return {
      rows: out,
      warehouses: 1,
      customers: targetCustomers.length,
      days: dates.length,
    };
  },
};
